<template>
  <div class="admin-reports-card">
    <div class="arc-glow"></div>
    <div class="row align-items-center position-relative g-3">
      <div class="col-lg-4">
        <div class="arc-badge"><i class="material-icons">auto_awesome</i> Autopilot</div>
        <h5 class="arc-title">Admin Reports</h5>
        <p class="arc-sub mb-0">
          See the scheduling work the platform is doing on your behalf
          <span v-if="scopeText">· {{ scopeText }}</span>
        </p>
      </div>
      <div class="col-lg-6">
        <div v-if="loading" class="text-white-50 small">Loading your autopilot summary...</div>
        <div v-else-if="stats">
          <div class="arc-period">{{ monthLabel }} · {{ stats.location_name }}</div>
          <div class="arc-stats">
          <div class="arc-stat"><strong><AnimatedNumber :value="stats.auto_scheduled?.total || 0" /></strong><span>positions auto-scheduled</span></div>
          <div class="arc-stat"><strong><AnimatedNumber :value="stats.substitutes?.total || 0" /></strong><span>substitutes auto-processed</span></div>
          <div class="arc-stat"><strong><AnimatedNumber :value="stats.swaps?.sent || 0" /></strong><span>swaps sent · {{ stats.swaps?.responded || 0 }} answered</span></div>
                   </div>
                 </div>
        <div v-else class="text-white-50 small">Summary unavailable right now.</div>
      </div>
      <div class="col-lg-2 text-lg-end">
        <button type="button" class="btn btn-light btn-sm mb-0" @click="open = true">
          <i class="material-icons align-middle" style="font-size:16px;">insights</i> View Reports
        </button>
      </div>
    </div>
    <AdminReportsModal v-model="open" :locationId="locationId" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import api from '@/config/api'
import AnimatedNumber from '@/components/AdminReports/AnimatedNumber.vue'
import AdminReportsModal from '@/components/AdminReports/AdminReportsModal.vue'

const props = defineProps({ locationId: [Number, String] })

const stats = ref(null)
const loading = ref(false)
const open = ref(false)

const scopeText = computed(() => ({
  activity: 'your activities',
  workgroup: 'your workgroups',
  instance: 'your events',
}[stats.value?.scope] || ''))

const monthLabel = computed(() => {
  const end = stats.value?.period?.end
  const d = end ? new Date(end + 'T00:00:00') : new Date()
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) + ' so far'
})

async function load() {
  if (!props.locationId) return
  loading.value = true
  try {
    stats.value = (await api.get(`/locations/${props.locationId}/admin-reports-stats`, { params: { period: 'month' } })).data?.data || null
  } catch (e) {
    stats.value = null
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.locationId, load)
</script>

<style scoped>
.admin-reports-card {
  position: relative; overflow: hidden; border-radius: 16px; padding: 22px 26px; color: #fff;
  background: linear-gradient(120deg, #0f172a 0%, #1e3a8a 55%, #0e7490 100%);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
}
.arc-glow {
  position: absolute; width: 220px; height: 220px; border-radius: 50%; background: #22c55e; filter: blur(60px);
  opacity: 0.35; top: -90px; right: 10%; animation: arc-float 7s ease-in-out infinite;
}
@keyframes arc-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(20px); } }
.arc-badge {
  display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.15);
  padding: 3px 10px; border-radius: 999px; font-size: 0.75rem; font-weight: 600; margin-bottom: 6px;
}
.arc-badge .material-icons { font-size: 14px; animation: arc-spin 6s linear infinite; }
@keyframes arc-spin { to { transform: rotate(360deg); } }
.arc-title { color: #fff; font-weight: 700; margin-bottom: 2px; }
.arc-sub { color: rgba(255,255,255,0.8); font-size: 0.85rem; }
.arc-period { font-size: 0.75rem; color: rgba(255,255,255,0.75); margin-bottom: 6px; font-weight: 600; }
.arc-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.arc-stat { background: rgba(255,255,255,0.12); border-radius: 12px; padding: 10px 12px; text-align: center; }
.arc-stat strong { display: block; font-size: 1.7rem; line-height: 1.1; color: #86efac; }
.arc-stat span { font-size: 0.72rem; opacity: 0.9; }
@media (max-width: 620px) { .arc-stats { grid-template-columns: 1fr; } }
@media (prefers-reduced-motion: reduce) { * { animation: none !important; } }
</style>
